import {_} from 'lodash';
import {ISyncField} from "../../index";
import {ObjectTypeId, TEXT_WRAPPER_END, TEXT_WRAPPER_START} from "../../constants";
import {ISyncfieldParameterDialog, ITextRange} from "./index";
import {nsFa} from "../fa/fn";
import {IDialogOptionsComponent} from "../dialog/index";
import {lm} from "../../appContext";

export namespace nsDynamicVariable {

    import i = nsFa.i;

    const DIALOG_PICK_SYNCFIELDID: IDialogOptionsComponent = {
        title: `${i('fa-shapes')} ((choose subject|s:((synchronization field|ac))))`,
        content: {
            name: 'component/dynamic-variable/dialog/pick/syncfield',
            params: {
                filterFn: (s: ISyncField) => s.name.charAt(0) != TEXT_WRAPPER_START,
                sortFn: (s: ISyncField) => s.name,
                objectTypeId: ObjectTypeId.FormData,
            },
        },
    }

    const DIALOG_PICK_MESSAGE_TEMPLATE_ID: IDialogOptionsComponent = {
        title: `${i('fa-message-flow')} ${lm.t(`choose subject|s:((messageflow|ac))`)}`,
        content: {
            name: 'component/dynamic-variable/dialog/pick/message-template',
            params: {},
        },
    }

    export const getSyncfieldParameterDialogObject = (syncfield: ISyncField, index: number): ISyncfieldParameterDialog | undefined => {
        const displayname = /\[(.*)\]/i.exec(syncfield.displayname ?? '')?.[1]?.split(';');
        const parameter = displayname?.[index]?.toLowerCase().split(' ').join('');
        switch (parameter) {
            case 'syncfieldid':
                return {
                    attribute: 'syncfieldid',
                    syncfieldid: syncfield.syncfieldid,
                    parameterDialog: _.cloneDeep(DIALOG_PICK_SYNCFIELDID),
                }
            case 'messageid':
                return {
                    attribute: 'id',
                    syncfieldid: syncfield.syncfieldid,
                    parameterDialog: _.cloneDeep(DIALOG_PICK_MESSAGE_TEMPLATE_ID),
                }
        }
    }

    export const getTextRange = (index: number, val: string): ITextRange | null => {
        let i: number;

        const start = (() => {
            i = index - 1;
            let counter = 0;
            while (i >= 0) {
                switch (val.charAt(i)) {
                    case TEXT_WRAPPER_START:
                        counter--;
                        break;
                    case TEXT_WRAPPER_END:
                        counter++;
                        break;
                }
                // we've found the opening position
                if (counter == -1) {
                    return i + 1; // add one to exclude the wrapper start character
                }
                i--;
            }
            return -1;
        })();
        if (start == -1) {
            return null;
        }

        const end = (() => {
            i = index;
            let counter = 0;
            while (i < val.length) {
                switch (val.charAt(i)) {
                    case TEXT_WRAPPER_START:
                        counter++;
                        break;
                    case TEXT_WRAPPER_END:
                        counter--;
                        break;
                }
                // we've found the closing position
                if (counter == -1) {
                    return i;
                }
                i++;
            }
            return -1;
        })();
        if (end == -1) {
            return null;
        }

        const str = val.substring(start, end);
        const wrapperEnd = end + 1;
        const wrapperStart = start - 1;
        const wrapperStr = val.substring(wrapperStart, wrapperEnd);

        return {
            caret: index,
            end,
            start,
            str,
            wrapperEnd,
            wrapperStart,
            wrapperStr,
        };
    }

    /**
     * Retrives a zero-based index of which parameter the caret is currently at.
     * @example given the following textRange/expression these are the potential parameter indexes:
     * Expression:      [RADIO;251;[AVAILABLEROLE;2216;Id,Name];Compilo]
     * Parameter index: [--0--;-1-;--------------2-------------;---3---]
     * @param textRange
     */
    export function getParameterIndex(textRange: ITextRange): number {
        let ret = 0;
        if (textRange) {
            const start = textRange.start;
            for (let i = start; i < textRange.caret; i++) {
                const currentIndex = i - start;
                const char = textRange.str.charAt(currentIndex);
                if (char == ';') {
                    ret++;
                }
                if (char == TEXT_WRAPPER_START) {
                    // skip any nested expressions
                    const subTextRange = getTextRange(i + 1, textRange.str);
                    if (subTextRange) {
                        i = subTextRange.wrapperEnd + start - 1;
                    }
                }
            }
        }
        return ret;
    }

    /**
     * Retrives the function name and arguments of a dynamic expression.
     * @example
     * Expression: [RADIO;251;[AVAILABLEROLE;2216;Id,Name];Compilo]
     * Returns: [{index: 1, str: 'RADIO'}, {index: 7, str: '251'}, {index: 11, str: '[AVAILABLEROLE;2216;Id,Name]'}, {index: 40, str: 'Compilo'}]
     */
    export function getArguments(exp: string): { index: number, str: string }[] {
        let nesting = 0
        let ret: any = []
        let lastIndex: number | null = null
        for (let i = 0; i < exp.length; i++) {
            const c = exp.charAt(i);
            if (c == TEXT_WRAPPER_START) {
                nesting++
            }
            if (c == TEXT_WRAPPER_END) {
                nesting--
            }
            if ((c == ';' && nesting == 1) || c == TEXT_WRAPPER_END && nesting == 0) {
                const str = exp.substring(lastIndex ?? 1, i)
                ret.push({index: lastIndex, str})
                lastIndex = i + 1;
            }
        }
        return ret;
    }

    export function validateExpression(exp: string): boolean | null {
        if (typeof exp !== 'string') {
            return null
        }
        if (exp.charAt(0) != TEXT_WRAPPER_START || exp.charAt(exp.length - 1) != TEXT_WRAPPER_END) {
            return false;
        }
        let nesting = 0
        for (let i = 0; i < exp.length; i++) {
            const c = exp.charAt(i);
            if (c == TEXT_WRAPPER_START) {
                nesting++
            }
            if (c == TEXT_WRAPPER_END) {
                nesting--
            }
        }
        return nesting === 0;
    }

}
